import {RuntimeAdapter} from '@assistant-ui/core/react';
import {Suggestions} from '@assistant-ui/core/store';
import {useAui} from '@assistant-ui/react';
import {AuiProvider} from '@assistant-ui/store';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {useCallback, useLayoutEffect, useMemo} from 'react';

import {wbAgentControllerStore} from '../../agentChat/controller';
import {WBAgentRuntime} from '../../agentChat/runtime/backend';
import {
  AgentChatStoreProvider,
  selectActiveThread,
  useAgentChatStore,
} from '../../agentChat/store';
import {useAgentChatRuntime} from '../../agentChat/useAgentChatRuntime';
import {useThreadReadTracker} from '../../agentChat/useThreadReadTracker';
import {useNavContext} from '../NavContextProvider';
import {WBAgentControllerBridge} from '../WBAgent/WBAgentControllerBridge';
import {SUGGESTION_ITEMS} from '../WBAgent/WBAgentSuggestionItem';
import {
  AgentChatAvailabilityContext,
  ShareTracesPolicyContext,
  useAgentChatAvailability,
  useComputeAgentChatAvailability,
  useComputeShareTracesPolicy,
} from './useAgentChatAvailability';

/**
 * AgentChatProvider supplies the chat store and availability to its subtree. The
 * AUI runtime is mounted separately, only when available, by
 * `AgentChatRuntimeBoundary`.
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();
  const shareTracesPolicy = useComputeShareTracesPolicy();

  // Mirror availability into the controller store so buttons anywhere in the
  // app can read it; the computed value is otherwise local to this subtree. A
  // layout effect updates the mirror before paint, so external buttons don't
  // show a stale-enabled frame after the feature turns off.
  useLayoutEffect(() => {
    wbAgentControllerStore.setAvailability(availability);
  }, [availability]);

  // Reset on unmount so a torn-down provider doesn't leave a stuck
  // 'available' with a dead handle.
  useLayoutEffect(
    () => () => {
      wbAgentControllerStore.setAvailability({
        isAvailable: false,
        unavailableReason: 'loading',
      });
    },
    []
  );

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      <ShareTracesPolicyContext.Provider value={shareTracesPolicy}>
        <AgentChatStoreProvider>{children}</AgentChatStoreProvider>
      </ShareTracesPolicyContext.Provider>
    </AgentChatAvailabilityContext.Provider>
  );
}

/**
 * AgentChatRuntimeBoundary mounts the AUI runtime and renders its children only
 * when the feature is available. Components that call `useAui` must render inside
 * it. Must itself render inside `AgentChatProvider`.
 */
export function AgentChatRuntimeBoundary({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element | null {
  const {isAvailable} = useAgentChatAvailability();
  if (!isAvailable) {
    return null;
  }

  return <AssistantRuntime>{children}</AssistantRuntime>;
}

function AssistantRuntime({children}: {children: React.ReactNode}) {
  const {params} = useNavContext();
  const wbAgentClient = useWbAgentClient(params.entityName, params.projectName);
  const store = useAgentChatStore();

  const getParentNodeId = useCallback(
    (nodeId: string) => {
      const thread = selectActiveThread(store.getState());
      return thread?.messageTree.parentId[nodeId] ?? null;
    },
    [store]
  );

  const agentRuntime = useMemo(
    () =>
      wbAgentClient ? new WBAgentRuntime(wbAgentClient, getParentNodeId) : null,
    [wbAgentClient, getParentNodeId]
  );

  const auiRuntime = useAgentChatRuntime(agentRuntime, params);
  useThreadReadTracker(store);
  const aui = useAui({
    threads: RuntimeAdapter(auiRuntime),
    suggestions: Suggestions(SUGGESTION_ITEMS),
  });

  return (
    <AuiProvider value={aui}>
      <WBAgentControllerBridge />
      {children}
    </AuiProvider>
  );
}
